import TrainingCoursesApi, {
  TrainingCoursesApi as ITrainingCoursesApi
} from "../../../../../services/api/v2/trainingCourses/TrainingCourses.api";
import { ITrainingCourseInfo } from "../../../../../services/hubs/ProjectDashboardHub/ProjectDashboard_types";
import { Mapper } from "../../view/ProjectView_mapper";
import { IModalContextModel } from "../../../../../core/modalZ/context/IModalContext";
import ModalContext from "../../../../../core/modalZ/context/ModalContext";
import LocalStorageService from "../../../../../services/local/localStorageService/LocalStorageService";
import { RagStatus } from "../../../../../enums";
import { action, computed, makeObservable, observable } from "mobx";
import { ReportFilterAddonsKeys } from "../../../../../core/reportFilter/ReportFilterAddon";
import _ from "lodash";

export class TrainingCourseReportingListViewModel {
  trainingCourseApi: ITrainingCoursesApi;
  organisationId: number;
  projectId: number;
  modalService: IModalContextModel;
  navigate: Function;
  page: string;
  @observable coursesData: ITrainingCourseInfo[] = [];
  @observable isLoading: boolean = true;
  @observable ragData: any[] = [];
  @observable totalCourses!: number;
  @observable ownerChartData: Array<Record<string, any>> = [];
  @observable stakeholderOwnerChartData: Array<Record<string, any>> = [];
  @observable trainingTypeChartData: Array<Record<string, any>> = [];
  @observable data: FP.Entities.ITrainingCourseSummary[] = [];
  @observable filteredData: FP.Entities.ITrainingCourseSummary[] = [];
  confidenceList: any = [];
  @observable dataConfidence: number = 0;
  @observable filterJSON: any = {};
  localStorage = LocalStorageService;
  private readonly initialBuckets = { red: 0, amber: 0, green: 0, completed: 0, unknown: 0 };

  @computed get hasFilters(): boolean {
    var res = false;
    _.forEach(this.filterJSON, (e: any) => {
      res = res || e.length > 0;
    });

    return res;
  }

  constructor(orgId: number, projectId: number, navigate: Function, page: string) {
    makeObservable(this);
    this.organisationId = orgId;
    this.projectId = projectId;
    this.trainingCourseApi = TrainingCoursesApi;
    this.modalService = ModalContext;
    this.navigate = navigate;
    this.page = page;

    let filters = this.localStorage.get(this.getStorageKey(orgId, projectId));
    if (filters) {
      this.setFilterJSON(JSON.parse(filters));
    }
  }

  onMount = async () => {
    this.setIsLoading(true);
    await this.loadCoursesData(this.projectId);
    this.setIsLoading(false);
  };

  onUnmount = () => {};

  loadCoursesData = async (projectId: number) => {
    const res = await this.trainingCourseApi.getGridData(this.organisationId, projectId, this.filterJSON);
    const courses = res.payload;
    if (!res.isError) {
      this.setData(courses);
    }
  };

  @action
  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @action
  onFilterChange = async k => {
    this.setFilterJSONFromList(k);
    const filteredData = this.data.filter(e => {
      const coursesFilterArray = this.filterJSON[ReportFilterAddonsKeys.CoursesGrid] || [];
      const actionsFilterPass = coursesFilterArray.length === 0 || coursesFilterArray.includes(e.id);
      return !!actionsFilterPass;
    });

    this.setFilteredData(filteredData);

    return null;
  };

  getBucket(c: ITrainingCourseInfo): string {
    if (c.trainingProgress === 100) {
      return "completed";
    }
    switch ((c as any).ragStatus) {
      case RagStatus.RED:
        return "red";
      case RagStatus.AMBER:
        return "amber";
      case RagStatus.GREEN:
        return "green";
      default:
        return "unknown";
    }
  }

  incrementBucket(map: Record<string, Record<string, number>>, key: string, bucket: string) {
    map[key] = map[key] || { ...this.initialBuckets };
    map[key][bucket]++;
  }

  @action
  setOwnerChartData(courses: ITrainingCourseInfo[]) {
    const byOwner: Record<string, Record<string, number>> = {};
    courses.forEach(c => {
      const owner = (c.owner || "").trim() || "Unassigned";
      const bucket = this.getBucket(c);
      this.incrementBucket(byOwner, owner, bucket);
    });
    this.ownerChartData = Object.entries(byOwner)
      .filter(([lbl]) => lbl !== "Unassigned")
      .map(([label, counts]) => ({ label, ...counts }));
  }

  @action
  setStakeholderOwnerChartData(courses: ITrainingCourseInfo[]) {
    const byStakeholderOwner: Record<string, Record<string, number>> = {};
    courses.forEach(c => {
      const raw = (c.projectStakeholderOwners || "").trim();
      const owners = raw
        ? raw
            .split("|")
            .map(n => n.trim())
            .filter(Boolean)
        : ["Unassigned"];
      const bucket = this.getBucket(c);
      owners.forEach(so => {
        this.incrementBucket(byStakeholderOwner, so, bucket);
      });
    });
    this.stakeholderOwnerChartData = Object.entries(byStakeholderOwner)
      .filter(([lbl]) => lbl !== "Unassigned")
      .map(([label, counts]) => ({ label, ...counts }));
  }

  @action
  setTrainingTypeChartData(courses: ITrainingCourseInfo[]) {
    const byType: Record<string, Record<string, number>> = {};
    courses.forEach(c => {
      const type = (c.trainingType || "").trim() || "Unassigned";
      const bucket = this.getBucket(c);
      this.incrementBucket(byType, type, bucket);
    });
    this.trainingTypeChartData = Object.entries(byType)
      .filter(([lbl]) => lbl !== "Unassigned")
      .map(([label, counts]) => ({ label, ...counts }));
  }

  setConfidenceList = (key: string, confidence: number) => {
    this.confidenceList.push(confidence);
    this.setDataConfidence();
  };

  @action
  setDataConfidence = () => {
    let allDataConfidence: number[] = this.confidenceList;
    this.dataConfidence = Math.round(
      allDataConfidence.reduce((partialSum, a) => partialSum + a, 0) / allDataConfidence.length
    );
  };

  @action
  setFilterJSON = (filterJSON: any) => {
    this.filterJSON = filterJSON;
    this.localStorage.set(this.getStorageKey(this.organisationId, this.projectId), JSON.stringify(filterJSON));
  };

  @action
  setFilterJSONFromList = (filterJSON: any[]) => {
    let s = {};

    filterJSON.forEach(e => {
      s[e.key] = e.ids;
    });
    this.setFilterJSON(s);
  };

  resetLocalStorage = () => {
    this.localStorage.remove(this.getStorageKey(this.organisationId, this.projectId));
  };

  getStorageKey = (organisationId, projectId) => {
    return `course-reporting-${organisationId}-${projectId}`;
  };

  setData = (data: FP.Entities.ITrainingCourseSummary[]) => {
    this.data = data;
    const ids = this.filterJSON[ReportFilterAddonsKeys.CoursesGrid] || [];
    const filtered = data.filter(e => ids.length === 0 || ids.includes(e.id));
    this.setFilteredData(filtered);
  };

  @action
  setFilteredData = (filteredData: FP.Entities.ITrainingCourseSummary[]) => {
    this.filteredData = filteredData;
    this.setRagData(filteredData);
    this.setOwnerChartData(filteredData);
    this.setStakeholderOwnerChartData(filteredData);
    this.setTrainingTypeChartData(filteredData);
  };

  @action
  setRagData = (courses: ITrainingCourseInfo[]) => {
    this.totalCourses = courses?.length ?? 0;
    this.ragData = Mapper.ragDataFromCourses(courses);
  };
}
